


















































































































































import axios from 'axios';
import { escapeRegExp, get } from 'lodash';

import EventBus from '../../../eventBus';
import CoRoundButton from '../../Atoms/co-round-button/CoRoundButton.vue';
import CoButton from '../../Atoms/co-button/CoButton.vue';
import CoText from '../../Atoms/co-text/CoText.vue';
import CoInput from '../../Molecules/co-input/CoInput.vue';
import CoCard from '../../Molecules/co-card/CoCard.vue';
import CoEditor from '../../Molecules/co-editor/CoEditor.vue';
import CoThumbnail from '../../Molecules/co-thumbnail/CoThumbnail.vue';
import CoAlert from '../../Molecules/co-alert/CoAlert.vue';
import CoModal from '../co-modal/CoModal.vue';
import CoContentPreview from '../co-content-preview/CoContentPreview.vue';
import CoUser from '../../Molecules/co-user/CoUser.vue';

interface UserProfile {
    Name?: string;
    ImageURL?: string;
}

interface User {
    ID: string;
    Slug?: string;
    Name?: string;
    Profile?: UserProfile;
}

interface SelectedTarget {
    type: 'thread' | 'user';
    id: string;
    title: string;
    subtitle: string;
    imageURL?: string;
    thumbnailType: 'user' | 'icon';
    icon?: string;
    threadID?: string;
    displayUser?: Record<string, unknown> | null;
    user?: User | null;
}

interface RawThread {
    object?: {
        ID?: string;
        GroupChat?: boolean;
        Title?: string;
        ImageURL?: string;
        Peer?: {
            ID?: string;
            Profile?: UserProfile;
        };
        Peers?: Array<{ ID?: string }>;
    };
    ID?: string;
    GroupChat?: boolean;
    Title?: string;
    ImageURL?: string;
    Peer?: {
        ID?: string;
        Profile?: UserProfile;
    };
    Peers?: Array<{ ID?: string }>;
}

interface EditorContent {
    Html?: string | null;
}

interface RecipientSuggestion {
    label: string;
    icon?: string;
    thumbnail?: string;
    value: {
        type: 'user';
        user?: User;
    };
}

export default {
    name: 'CoShareInMessenger',
    components: {
        CoRoundButton,
        CoButton,
        CoText,
        CoInput,
        CoEditor,
        CoThumbnail,
        CoAlert,
        CoModal,
        CoContentPreview,
        CoUser,
        CoCard,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        url: {
            type: String,
            default: '',
        },
        contentID: {
            type: String,
            default: '',
        },
        contentSlug: {
            type: String,
            default: '',
        },
        text: {
            type: String,
            default: '',
        },
        message: {
            type: String,
            default: '',
        },
        previewImage: {
            type: String,
            default: '',
        },
        previewFavicon: {
            type: String,
            default: '',
        },
        previewSubtitle: {
            type: String,
            default: '',
        },
        threads: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            sharing: false,
            selectedTarget: null as SelectedTarget | null,
            errorMessage: '',
            noteContent: null as EditorContent | null,
            fetchedThreads: [] as RawThread[],
            threadsLoading: false,
        };
    },
    computed: {
        availableThreads() {
            // Keep the backend ordering untouched because the thread list is already returned by recent activity.
            return this.fetchedThreads.length > 0 ? this.fetchedThreads : this.threads;
        },
        messagePreviewTitle() {
            return this.title || this.text || this.url;
        },
        messagePreviewUrl() {
            return this.url;
        },
        previewDomain() {
            try {
                return this.url ? new URL(this.url).hostname.replace(/^www\./, '') : '';
            } catch (error) {
                return '';
            }
        },
        sharedContentType() {
            if (!this.url) {
                return null;
            }

            try {
                const url = new URL(this.url, window.location.origin);
                const firstSegment = (url.pathname.split('/').filter(Boolean)[0] || '').toLowerCase();
                switch (firstSegment) {
                    case 'event':
                    case 'events':
                        return 'event';
                    case 'page':
                    case 'pages':
                    case 'project':
                    case 'projects':
                    case 'channel':
                    case 'channels':
                        return 'page';
                    case 'post':
                        return 'post';
                    case 'job':
                    case 'jobs':
                        return 'job';
                    default:
                        return null;
                }
            } catch (error) {
                return null;
            }
        },
        hasSharePreview() {
            return !!(this.title || this.text || this.url);
        },
        contentPreviewFetch() {
            if (!this.url) {
                return null;
            }

            return {
                Slug: this.url,
            };
        },
        canShare() {
            return !!this.selectedTarget && !this.disabled && !this.sharing && this.hasSharePreview;
        },
        shareMessage() {
            const presetMessage = this.message ? `<p>${this.escapeHtml(this.message)}</p>` : '';
            const note = this.noteContent?.Html || '';
            const title = this.escapeHtml(this.title || this.text || '');
            const url = this.escapeAttribute(this.url);
            const linkLabel = this.escapeHtml(
                [this.$t('labels.share'), title || this.$t(`labels.${this.sharedContentType}`)].join(': ')
            );
            const parts = [];

            if (url) {
                parts.push(`<p><a href="${url}">${linkLabel}</a></p>`);
            } else if (title) {
                parts.push(`<p>${title}</p>`);
            }
            if (presetMessage) {
                parts.push(presetMessage);
            }
            if (note && note !== '<p></p>') {
                parts.push(note);
            }

            return parts.join('');
        },
    },
    methods: {
        get,
        show() {
            this.loadThreads();
            this.$refs.modal?.show();
        },
        hide() {
            this.$refs.modal?.hide();
        },
        onClose() {
            this.$emit('close');
        },
        matchesQuery(values: string[] = [], query: string) {
            if (!query) return true;

            try {
                const matcher = new RegExp(escapeRegExp(query), 'i');
                return values.some((value) => matcher.test(value || ''));
            } catch (error) {
                return values.some((value) => (value || '').toLowerCase().includes(query.toLowerCase()));
            }
        },
        hasExistingDirectThreadWithUser(userID: string) {
            return this.availableThreads.some(
                (thread: RawThread) => get(thread, 'object.Peer.ID', get(thread, 'Peer.ID')) === userID
            );
        },
        getUserName(user: User) {
            return user?.Profile?.Name || user?.Name || '';
        },
        getUserImage(user: User) {
            return user?.Profile?.ImageURL || '';
        },
        async fetchUsers(query: string) {
            if (!query || query.length < 2) {
                return [];
            }

            const response = await axios({
                method: 'GET',
                url: `/search/user/${query}`,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const meID = get(this.$store, 'state.me.ID', null);
            return (response?.data || []).filter((user: User) => user.ID !== meID);
        },
        async loadThreads() {
            if (this.threadsLoading || this.fetchedThreads.length > 0) {
                return;
            }

            this.threadsLoading = true;
            let url = '/chat/t/list';
            if (get(this.$store, 'state.circlesOn', false)) {
                url = '/chat/t/listV2';
            }

            try {
                const response = await axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                this.fetchedThreads = get(response, 'data.objects', []);
            } catch (error) {
                this.fetchedThreads = [];
            } finally {
                this.threadsLoading = false;
            }
        },
        async searchUsers(query: string) {
            const users = await this.fetchUsers(query);
            return users.map((user: User) => ({
                label: this.getUserName(user),
                thumbnail: this.getUserImage(user),
                value: {
                    type: 'user',
                    user,
                },
            }));
        },
        selectCandidate(suggestion: RecipientSuggestion) {
            const payload = suggestion?.value;
            if (!payload) return;

            if (payload.type === 'user' && payload.user) {
                this.selectUser(payload.user);
            }
        },
        selectThread(thread: RawThread) {
            const rawThread = thread?.object || thread;
            const peersCount = get(rawThread, 'Peers.length', 0);
            const isGroupChat = !!rawThread?.GroupChat;
            const peer = get(rawThread, 'Peer', null);
            const profile = get(rawThread, 'Peer.Profile', null);
            this.selectedTarget = {
                type: 'thread',
                id: get(rawThread, 'ID', null),
                threadID: get(rawThread, 'ID', null),
                title: isGroupChat
                    ? rawThread?.Title || (this.$t('labels.suggestedChats') as string)
                    : get(rawThread, 'Peer.Profile.Name', '') || (this.$t('labels.suggestedChats') as string),
                subtitle: isGroupChat
                    ? `${peersCount} ${this.$tc('labels.member', peersCount)}`
                    : (this.$t('labels.suggestedChats') as string),
                imageURL: isGroupChat ? rawThread?.ImageURL : get(rawThread, 'Peer.Profile.ImageURL', ''),
                thumbnailType: isGroupChat ? 'icon' : 'user',
                icon: isGroupChat ? 'people' : null,
                displayUser:
                    !isGroupChat && peer
                        ? {
                              ...peer,
                              ...profile,
                              Name: get(profile, 'Name', null),
                              Firstname: get(profile, 'FirstName', null),
                              Lastname: get(profile, 'LastName', null),
                              ImageURL: get(profile, 'ImageURL', null),
                          }
                        : null,
                user: null,
            };
            this.$emit('select-thread', get(rawThread, 'ID', null));
        },
        selectUser(user: User) {
            this.selectedTarget = {
                type: 'user',
                id: user.ID,
                title: this.getUserName(user),
                subtitle: this.$t('labels.newconversation') as string,
                imageURL: this.getUserImage(user),
                thumbnailType: 'user',
                displayUser: {
                    ...user,
                    ...get(user, 'Profile', {}),
                    Name: get(user, 'Profile.Name', null),
                    Firstname: get(user, 'Profile.FirstName', null),
                    Lastname: get(user, 'Profile.LastName', null),
                    ImageURL: get(user, 'Profile.ImageURL', null),
                },
                user,
            };
            this.$emit('select-user', user);
        },
        clearSelection() {
            this.selectedTarget = null;
            this.$nextTick(() => {
                this.$refs.recipientInput?.focus?.();
            });
        },
        async createThread(user: User) {
            const meID = get(this.$store, 'state.me.ID', null);
            const thread = {
                Participants: [meID, user.ID],
            };

            const response = await axios({
                method: 'POST',
                url: '/chat/t',
                withCredentials: true,
                data: JSON.stringify(thread),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            this.$emit('thread-created', response?.data);
            return response?.data?.ID;
        },
        async sendMessage(threadID: string) {
            const newMessage = {
                From: get(this.$store, 'state.me.ID', null),
                ThreadID: threadID,
                Text: this.shareMessage,
                SpaceID: get(this.$store, 'state.space.ID', null),
            };

            const response = await axios.post('/chat/msgs/send', newMessage);
            return response?.data;
        },
        async share() {
            if (!this.canShare) {
                return;
            }

            this.sharing = true;
            this.errorMessage = '';

            try {
                let { threadID } = this.selectedTarget as SelectedTarget;

                if (!threadID && this.selectedTarget?.type === 'user' && this.selectedTarget.user) {
                    threadID = await this.createThread(this.selectedTarget.user);
                }

                if (!threadID) {
                    throw new Error('No thread ID available');
                }

                const message = await this.sendMessage(threadID);
                this.$emit('shared', {
                    threadID,
                    message,
                    target: this.selectedTarget,
                });
                // track share activity event
                this.recordActivity();
                this.noteContent = null;
                this.$refs.messageEditor?.clear?.(true, true);
                EventBus.$emit('INFO', {
                    Message: this.$t('messages.shareInMessengerSuccess'),
                });
                this.hide();
            } catch (error) {
                this.errorMessage = this.$t('messages.shareInMessengerFailed') as string;
                EventBus.$emit('ERROR', {
                    Message: this.$t('messages.shareInMessengerFailed'),
                });
                this.$emit('error', error);
            } finally {
                this.sharing = false;
            }
        },
        escapeHtml(value: string) {
            return (value || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
        escapeAttribute(value: string) {
            return this.escapeHtml(value).replace(/`/g, '&#96;');
        },
        recordActivity(funnel = 'share_activity', step = 'feature_click', feature_key = 'share_in_message') {
            const route = this.$route || null;
            const location = route?.path || window.location.pathname;
            const fullPath = route?.fullPath || `${window.location.pathname}${window.location.search}`;

            axios
                .post('/me/activity/funnel', {
                    funnel,
                    step,
                    feature_key,
                    location,
                    full_path: fullPath,
                    parameters: {
                        contentType: this.sharedContentType,
                        contentSlug: this.contentSlug,
                        contentURl: this.url,
                        contentID: this.contentID,
                    },
                })
                .catch((error) => {
                    console.error('Failed to record activity', error);
                });
        },
    },
};
